import { Box, Grid, IconButton, Stack, Typography } from "@mui/material";
import CustomButton from "components/atoms/CustomButton";

import RichTextEditor from "components/atoms/richTextEditor/RichTextEditor";
import VoiceNotePlayBack from "components/atoms/voiceNotePlayback";
import VoiceRecorder from "components/atoms/VoiceRecorder";
import { useFormik } from "formik";
import React from "react";
import { useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useCustomMutation } from "store/hooks/react-query/mutate/useMutateFunc";
import { useCustomQuery } from "store/hooks/react-query/query/useQuery";
import { GET_PATIENT_RECENT_VISIT } from "utils/reactQueryKeys";
import DeleteIcon from "@mui/icons-material/Delete";
import useCloudinaryUploader from "hooks/useUploadToCloudinary";
import secureLocalStorage from "react-secure-storage";
import { debounce } from "utils/debounce";

const initialValues = {
  history: "",
  examination: "",
  findings: "",
  diagnosis: "",
  plan: "",
};

const RecordVoiceNote = ({ setAudioObjArr }) => {
  return (
    <Stack direction={"column"} gap={3}>
      <Typography
        sx={{
          fontWeight: "700",
          lineHeight: "18px",
          color: "primary.formLabel",
          fontSize: "16px",
        }}
      >
        Record voice note
      </Typography>

      <VoiceRecorder setAudioObjArr={setAudioObjArr} />
    </Stack>
  );
};

function DoctorsNote({ handleNext }) {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [audioObjArr, setAudioObjArr] = React.useState([]);

  const savedValues = secureLocalStorage.getItem(`hms_patient_${id}`) || null;

  const {
    isError,
    isLoading: cloudinaryUploadLoading,
    uploadToCloudinary,
  } = useCloudinaryUploader();

  const { handleChange, values } = useFormik({
    initialValues: savedValues || initialValues,
  });

  //get recent visits
  const { data: recentVisit } = useCustomQuery(
    [GET_PATIENT_RECENT_VISIT, id],
    {
      url: `/visit/recent/${id}`,
      method: "get",
    },
    {
      refetchOnWindowFocus: false,
    }
  );

  //patch visit {assesmentlog}
  const { mutateAsync, isLoading } = useCustomMutation(
    {
      url: `/visit/${recentVisit?.data?._id}`,
      method: "patch",
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([GET_PATIENT_RECENT_VISIT, id]);
        toast.success("Success");

        secureLocalStorage.removeItem(`hms_patient_${id}`);
        handleNext();
      },

      onError: (error) => {
        if (Array.isArray(error.message)) {
          return error.message.map((msg) => toast.error(msg));
        }

        return toast.error(error.message);
      },
    }
  );

  const onDeleteVN = (audioUrl) => {
    setAudioObjArr((prev) =>
      prev.filter((_audio) => _audio.audioUrl !== audioUrl)
    );
  };

  const handleAdd = async () => {
    const vns = await uploadToCloudinary(
      audioObjArr.map((_audioObjArr) => _audioObjArr.audioBlob),
      "video"
    );

    mutateAsync({ assessmentLog: [{ note: values, voiceNotes: [...vns] }] });
  };

  const debouncedSave = debounce((e) => {
    const data = {
      ...values,
      [e.target.name]: e.target.value,
    };

    secureLocalStorage.setItem(`hms_patient_${id}`, data);
  }, 1000);

  const onChange = (e) => {
    handleChange(e);
    debouncedSave(e);
  };

  return (
    <Box>
      <Stack
        sx={{
          width: {
            xs: "100%",
            sm: "90%",
            lg: "68%",
          },
          mt: 2,
        }}
        direction={"column"}
        gap={4}
      >
        <RichTextEditor
          handleChange={onChange}
          value={values.history}
          name="history"
          title="History"
          placeholder="Start typing here"
        />
        <RichTextEditor
          handleChange={onChange}
          value={values.examination}
          name="examination"
          title="Examination"
          placeholder="Start typing here"
        />
        <RichTextEditor
          handleChange={onChange}
          value={values.findings}
          name="findings"
          title="Findings"
          placeholder="Start typing here"
        />
        <RichTextEditor
          handleChange={onChange}
          value={values.diagnosis}
          name="diagnosis"
          title="Diagnosis"
          placeholder="Start typing here"
        />
        <RichTextEditor
          handleChange={onChange}
          value={values.plan}
          name="plan"
          title="Plan"
          placeholder="Start typing here"
        />

        <RecordVoiceNote setAudioObjArr={setAudioObjArr} />

        <Grid container rowSpacing={2} columnSpacing={5} sx={{ mt: 1 }}>
          {audioObjArr.map((audio) => (
            <Grid item xs={12} sm={6} key={audio.audioUrl}>
              <Stack
                direction={"row"}
                alignItems={"center"}
                justifyContent={"space-between"}
              >
                <VoiceNotePlayBack audioUrl={audio.audioUrl} />
                <IconButton
                  size="small"
                  onClick={onDeleteVN.bind(null, audio.audioUrl)}
                >
                  <DeleteIcon style={{ color: "#E64034" }} />
                </IconButton>
              </Stack>
            </Grid>
          ))}
        </Grid>
      </Stack>
      <Stack direction={"row"} justifyContent="space-between" mt={3}>
        <CustomButton
          text={"Save Tab"}
          sx={{ minWidth: "30%" }}
          disabled={
            isLoading ||
            !values.diagnosis ||
            !values.history ||
            !values.examination ||
            !values.findings ||
            !values.plan ||
            cloudinaryUploadLoading
          }
          onClick={handleAdd}
          color="secondary"
        />
        <CustomButton
          text={"Skip"}
          onClick={handleNext}
          variant="containedBrown"
          sx={{ minWidth: "30%" }}
        />
      </Stack>
    </Box>
  );
}

export default DoctorsNote;
